import { TrashIcon } from '@heroicons/react/20/solid';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import type { XiorError } from 'xior';
import {
  getGetApiPickemsCurrentPlayerBetableQueryKey,
  usePutApiPickemsBets,
} from '@/api/backoffice/pickems/pickems';
import type { PickemsDto, UserDetailDto } from '@/api/backoffice-schemas';
import { Button } from '@/components/button';
import NoPickems from '@/components/noPickems';
import type { EnhancedPickemDto } from '@/components/pickems/types';
import { useAuthDialog } from '@/context/AuthDialogProvider';
import usePlayer from '@/hooks/usePlayer.hook';
import { PickedPickem } from './PickedPickem';

interface PickedPickemsListProps {
  selectedPickems: EnhancedPickemDto[];
  totalPickems: number;
  onClearAll: () => void;
  onRemove: (pickemId: string) => void;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

const getSubmitButtonText = (
  isPending: boolean,
  player: UserDetailDto | undefined
) => {
  if (player) {
    return isPending ? 'Submitting...' : 'Lock in Picks';
  }
  return 'Register to submit';
};

export const PickedPickemsList = ({
  selectedPickems,
  onClearAll,
  onRemove,
  onSuccess,
  onError,
}: PickedPickemsListProps) => {
  const { toggleIsOpen } = useAuthDialog();
  const { data: player } = usePlayer();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = usePutApiPickemsBets({
    mutation: {
      onSuccess: () => {
        // Individual bet success is handled in Promise.all
        onSuccess?.();
        queryClient.invalidateQueries();
      },
      onError: (error: XiorError) => {
        toast.error(error.response?.data.message || error.name);
        onError?.(error);
      },
    },
  });

  const handleSubmit = async () => {
    if (!player) {
      toggleIsOpen('sign-up');
      return;
    }

    try {
      const bets = selectedPickems.map((pickem) => {
        if (!(pickem.match?.teamMatches?.[0] && pickem.selectedWinnerId)) {
          throw new Error(`Invalid pickem data for ID: ${pickem.id}`);
        }

        const betCommand: PickemsDto = {
          pickemId: pickem.id ?? '',
          draw: false,
          playerWinnerChoiceId: pickem.selectedWinnerId,
          isCancelled: false,
        };

        return betCommand;
      });

      await mutateAsync({ data: { pickems: bets } });

      toast.success('Pickems Locked In!');

      await queryClient.refetchQueries({
        // Refacth all
        queryKey: getGetApiPickemsCurrentPlayerBetableQueryKey(),
      });

      onSuccess?.();
    } catch (error) {
      onError?.(error);
    }
  };

  if (selectedPickems.length === 0) {
    return <NoPickems />;
  }

  return (
    <div>
      <div className="mb-4 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="font-medium">Picks</h2>
          <div className="flex items-center gap-2">
            <TrashIcon
              className="h-6 w-6 cursor-pointer text-blue-gray"
              onClick={onClearAll}
            />
          </div>
        </div>
      </div>
      <div className="mb-4 flex flex-col gap-4">
        {selectedPickems.map((pickem) => (
          <PickedPickem key={pickem.id} onRemove={onRemove} pickem={pickem} />
        ))}
      </div>

      <Button
        className="w-full"
        disabled={isPending}
        onClick={handleSubmit}
        variant="primary"
      >
        {getSubmitButtonText(isPending, player?.data)}
      </Button>
    </div>
  );
};
